import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper,
  Container,CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { collection, getDocs, query, where,} from "firebase/firestore";
import { db } from "../../../firebase";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(4),
}));

const SalesSummary = ({ unitCode, companyCode, unitType, companyName,}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const queryParams = new URLSearchParams(location.search);

  const urlUnitCode =
    queryParams.get("unit") || location.state?.unitCode || "";

  const effectiveUnitCode = urlUnitCode || unitCode;

  const [salesData, setSalesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [grandTotal, setGrandTotal] = useState(0);
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [payMode, setPayMode] = useState("All");
  const [payModes, setPayModes] = useState([]);

  useEffect(() => {
    const fetchPayModes = async () => {
        try {
        const preferenceQuery = query(
            collection(db, "preferences"),
            where("companyCode", "==", companyCode),
            where("unitCode", "==", effectiveUnitCode),
            where("preferenceName", "==", "Payment")
        );

        const snapshot = await getDocs(preferenceQuery);

        if (!snapshot.empty) {
  const data = snapshot.docs[0].data();

  const modes = data.preferenceOptions || [];

  if (Array.isArray(modes)) {
    setPayModes(modes);
  }
}
        } catch (err) {
        console.error("Error fetching payment modes:", err);
        }
    };

        if (effectiveUnitCode) {
            fetchPayModes();
        }
    }, [effectiveUnitCode]);

  useEffect(() => {
    const fetchSalesSummary = async () => {
      if (!effectiveUnitCode) {
        setError("Unit code is missing.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const ordersQuery = query(
          collection(db, "orders"),
          where("unitCode", "==", effectiveUnitCode),
        );

        const snapshot = await getDocs(ordersQuery);

      const ordersData = snapshot.docs
        .map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }))
        .filter((order) => {
            if (
            order.billNo === undefined ||
            order.billNo === null ||
            order.billNo === "" ||
            Number(order.billNo) <= 0
            ) {
            return false;
            }

            if (!order.createdAt?.toDate) {
            return false;
            }

            const billDate = order.createdAt
            .toDate()
            .toISOString()
            .split("T")[0];

            const matchesDate = billDate >= startDate && billDate <= endDate;

            const matchesPayMode =
                payMode === "All" ||
                (order.paymentMethod || "").toLowerCase() ===
                    payMode.toLowerCase();

            return matchesDate && matchesPayMode;
        });

        const sortedOrders = ordersData.sort(
          (a, b) => Number(a.billNo || 0) - Number(b.billNo || 0)
        );

        setSalesData(sortedOrders);

        const total = sortedOrders.reduce(
          (sum, order) => sum + Number(order.grandTotal || 0),
          0
        );

        setGrandTotal(total);

        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load sales summary.");
        setIsLoading(false);
      }
    };

    fetchSalesSummary();
  }, [effectiveUnitCode, startDate, endDate, payMode]);

  const handleExportExcel = () => {
    const excelData = salesData.map((order, index) => ({
      "Sl No": index + 1,
      "Bill No": order.billNo || "N/A",
      "Bill Date": order.createdAt?.toDate
        ? order.createdAt.toDate().toLocaleDateString("en-GB")
        : "N/A",
      "Bill Amount":`₹ ${Number(order.grandTotal || 0).toFixed(2)}`,
      "Paymode": order.paymentMethod || "Cash",
      "Pay Collected": `₹ ${Number(order.grandTotal || 0).toFixed(2)}`,
    }));

    // Add Grand Total Row
    excelData.push({
      "Sl No": "",
      "Bill No": "",
      "Bill Date": "Grand Total",
      "Bill Amount":`₹ ${grandTotal.toFixed(2)}`,
      "Paymode": "Total Pay",
      "Pay Collected":`₹ ${grandTotal.toFixed(2)}`,
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
   const headers = ["A1", "B1", "C1", "D1", "E1", "F1"];

headers.forEach((cell) => {
  if (worksheet[cell]) {
    worksheet[cell].s = {
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
    };
  }
});
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

for (let row = range.s.r + 1; row <= range.e.r; row++) {
  // Bill Amount column (D)
  const billAmountCell = worksheet[`D${row + 1}`];

  if (billAmountCell) {
    billAmountCell.s = {
      alignment: {
        horizontal: "right",
        vertical: "center",
      },
    };
  }

  // Pay Collected column (F)
  const payCollectedCell = worksheet[`F${row + 1}`];

  if (payCollectedCell) {
    payCollectedCell.s = {
      alignment: {
        horizontal: "right",
        vertical: "center",
      },
    };
  }
}

    worksheet["!cols"] = [
      { wch: 10 }, // Sl No
      { wch: 12 }, // Bill No
      { wch: 15 }, // Bill Date
      { wch: 18 }, // Bill Amount
      { wch: 15 }, // Paymode
      { wch: 18 }, // Pay Collected
    ];
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Sales Summary"
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const data = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

      const today = new Date();

      const formattedDate = today.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "_");

      saveAs(
        data,
        `Sales_Summary_${formattedDate}.xlsx`
      );
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Summary</title>

          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }

            h2 {
              text-align: center;
              margin-bottom: 20px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            table, th, td {
              border: 1px solid #000;
            }

            th, td {
              padding: 10px;
              text-align: center;
            }

            .grand-total {
              margin-top: 20px;
              text-align: right;
              font-size: 18px;
              font-weight: bold;
            }
          </style>
        </head>

        <body>
          <h2>
            Sales Summary - ${companyName || "Unknown Company"}
          </h2>

          <table>
            <tr>
              <th>Sl No</th>
              <th>Bill No</th>
              <th>Bill Date</th>
              <th>Bill Amount</th>
              <th>Paymode</th>
              <th>Pay Collected</th>
            </tr>

            ${salesData
              .map(
                (order, index) => `
                  <tr>
                    <td>${index + 1}</td>

                    <td>${order.billNo || "N/A"}</td>

                    <td>
                      ${
                        order.createdAt?.toDate
                          ? order.createdAt
                              .toDate()
                              .toLocaleDateString("en-GB")
                          : "N/A"
                      }
                    </td>

                    <td>
                      ₹ ${Number(order.grandTotal || 0).toFixed(2)}
                    </td>

                    <td>
                      ${order.paymentMethod || "Cash"}
                    </td>

                    <td>
                      ₹ ${Number(order.grandTotal || 0).toFixed(2)}
                    </td>
                  </tr>
                `
              )
              .join("")}
          </table>

          <div class="grand-total">
            Grand Total : ₹ ${grandTotal.toFixed(2)}
          </div>

          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleBack = () => {
    navigate("/unit_manager/home", {
      state: {
        unitCode: effectiveUnitCode,
        companyCode,
        companyName,
        unitType,
      },
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />

        <Typography sx={{ mt: 2 }}>
          Loading sales summary...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box ref={printRef}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            mb: 4,
            textAlign: "center",
          }}
        >
          Sales Summary - {companyName}
        </Typography>

        <Box
            sx={{
                display: "flex",
                gap: 2,
                mb: 3,
                flexWrap: "wrap",
                justifyContent: "center",
            }}
        >
        <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{
            shrink: true,
            }}
        />

        <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{
            shrink: true,
            }}
        />

        <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Pay Mode</InputLabel>

            <Select
                value={payMode}
                label="Pay Mode"
                onChange={(e) => setPayMode(e.target.value)}
            >
                <MenuItem value="All">All</MenuItem>

                {payModes.map((mode, index) => (
                <MenuItem key={index} value={mode}>
                    {mode}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </Box>
        <StyledCard>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.light" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Sl No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Bill No</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Bill Date</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Bill Amount</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Paymode</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Pay Collected</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {salesData.length > 0 ? (
                  salesData.map((order, index) => (
                    <TableRow
                      key={order.id}
                      sx={{
                        bgcolor: index % 2 === 0 ? "#fafafa" : "#fff",
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{order.billNo || "N/A"}</TableCell>
                      <TableCell>
                        {order.createdAt?.toDate
                          ? order.createdAt
                              .toDate()
                              .toLocaleDateString("en-GB")
                          : "N/A"}
                      </TableCell>
                      <TableCell>₹ {Number(order.grandTotal || 0).toFixed(2)}</TableCell>
                      <TableCell>{order.paymentMethod || "Cash"}</TableCell>
                      <TableCell>₹ {Number(order.grandTotal || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No sales records found
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      fontSize: 16,
                    }}
                  >
                    Grand Total
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: 16,
                    }}
                  >
                    ₹ {grandTotal.toFixed(2)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      fontSize: 16,
                    }}
                  >
                    Total Pay
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      fontSize: 16,
                    }}
                  >
                  ₹ {grandTotal.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleExportExcel}
        >
          Excel
        </Button>
        <Button
          variant="outlined"
          onClick={handleBack}
        >
          Back
        </Button>
      </Box>
    </Container>
  );
};

export default SalesSummary;